import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, ASSET_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth, getUser, type UserRole } from '../utils/auth';
import type { ApiResponse, VideoDescriptor } from '../types/api';
import type { VideoCollectionMembership } from '../types/collection';
import { getVideoCoverUrl, handleCoverError } from '../utils/videoCover';
import AppShell from './AppShell';
import { Select, SelectItem } from './Select';
import { Input } from './Input';
import { Lock, Globe } from 'lucide-react';
import './VideoEdit.css';

const VideoEdit: React.FC = () => {
  const [video, setVideo] = useState<VideoDescriptor | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // 编辑状态
  const [editingName, setEditingName] = useState<string>('');
  const [editingVisibility, setEditingVisibility] = useState<'private' | 'public'>('private');
  const [editingWordMode, setEditingWordMode] = useState<'raw' | 'display'>('display');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // 合集成员关系
  const [memberships, setMemberships] = useState<VideoCollectionMembership[]>([]);
  const [loadingMemberships, setLoadingMemberships] = useState(false);
  // 单条 checkbox 的乐观锁，避免快速点击重复发请求
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // 从URL参数或location state中获取videoId
    const params = new URLSearchParams(location.search);
    const videoId = params.get('videoId');

    if (videoId) {
      loadVideo(videoId);
    } else {
      navigate('/profile');
    }
  }, [location, navigate]);

  const loadVideo = async (videoId: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/video/${videoId}`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('获取视频信息失败');
      }

      const data: ApiResponse<VideoDescriptor> = await response.json();
      if (data.success && data.data) {
        setVideo(data.data);
        setEditingName(data.data.name || data.data.videoId);
        setEditingVisibility(data.data.visibility || 'private');
        setEditingWordMode(data.data.wordHighlightMode === 'raw' ? 'raw' : 'display');
        loadMemberships(data.data.videoId);
      } else {
        window.alert(data.error || '获取视频信息失败');
        navigate('/video-management');
      }
    } catch (err) {
      console.error('加载视频失败:', err);
      window.alert('网络错误，请稍后重试');
      navigate('/video-management');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 拉取当前用户的所有合集，附带"此视频是否已在该合集"的标记。
   */
  const loadMemberships = async (videoId: string) => {
    setLoadingMemberships(true);
    try {
      const resp = await fetch(
        `${API_BASE_URL}/videos/${encodeURIComponent(videoId)}/collections?mine=1`,
        { headers: getAuthHeaders() },
      );
      if (resp.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      if (!resp.ok) return;
      const payload = (await resp.json()) as ApiResponse<{ list: VideoCollectionMembership[] }>;
      if (payload.success && payload.data) {
        setMemberships(payload.data.list || []);
      }
    } catch (err) {
      console.error('加载合集关系失败:', err);
    } finally {
      setLoadingMemberships(false);
    }
  };

  /**
   * 勾选/取消勾选某个合集
   * - 未在合集且勾选：POST /api/collections/:cid/items
   * - 已在合集且取消：DELETE /api/collections/:cid/items/:iid
   */
  const handleToggleCollection = async (m: VideoCollectionMembership) => {
    if (!video) return;
    if (togglingId === m.id) return;
    setTogglingId(m.id);
    try {
      if (m.inCollection && m.itemId) {
        const resp = await fetch(
          `${API_BASE_URL}/collections/${m.id}/items/${m.itemId}`,
          { method: 'DELETE', headers: getAuthHeaders() },
        );
        if (resp.status === 401) {
          clearAuth();
          navigate('/login');
          return;
        }
        const data = (await resp.json()) as ApiResponse;
        if (data.success) {
          setMemberships((prev) =>
            prev.map((it) =>
              it.id === m.id
                ? { ...it, inCollection: false, itemId: null, position: null }
                : it,
            ),
          );
        } else {
          window.alert(data.error || '从合集中移除失败');
        }
      } else {
        const resp = await fetch(`${API_BASE_URL}/collections/${m.id}/items`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoId: video.videoId }),
        });
        if (resp.status === 401) {
          clearAuth();
          navigate('/login');
          return;
        }
        const data = (await resp.json()) as ApiResponse<{ itemId: string; position: number }>;
        if (data.success && data.data) {
          const { itemId, position } = data.data;
          setMemberships((prev) =>
            prev.map((it) =>
              it.id === m.id
                ? { ...it, inCollection: true, itemId, position }
                : it,
            ),
          );
        } else {
          window.alert(data.error || '加入合集失败');
        }
      }
    } catch (err) {
      console.error('切换合集状态失败:', err);
      window.alert('网络错误，请稍后重试');
    } finally {
      setTogglingId(null);
    }
  };
 
  // Cover upload
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [localCoverPreview, setLocalCoverPreview] = useState<string | null>(null);

  const handleCoverClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCoverSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !video) return;

    // Preview immediately
    const previewUrl = URL.createObjectURL(file);
    setLocalCoverPreview(previewUrl);
    setUploadingCover(true);

    try {
      const form = new FormData();
      form.append('videoId', video.videoId);
      form.append('cover', file);

      const headers = { ...(getAuthHeaders() as Record<string, string>) };
      // Remove Content-Type so the browser will set multipart/form-data with boundary
      delete headers['Content-Type'];

      const resp = await fetch(`${API_BASE_URL}/video/update-cover`, {
        method: 'POST',
        headers,
        body: form,
      });

      if (resp.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      const result: ApiResponse<{ coverUrl?: string }> = await resp.json();
      if (result.success) {
        // refresh video data if backend provides coverUrl or re-load
        if (result.data?.coverUrl) {
          setVideo(prev => prev ? { ...prev, coverUrl: result.data!.coverUrl } : prev);
        } else {
          // reload full video descriptor
          await loadVideo(video.videoId);
        }
        setSuccessMessage('封面已更新');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setLocalCoverPreview(null);
        window.alert(result.error || '封面上传失败');
      }
    } catch (err) {
      console.error('封面上传失败', err);
      setLocalCoverPreview(null);
      window.alert('上传失败，请稍后重试');
    } finally {
      setUploadingCover(false);
      // clear file input value so selecting same file again will trigger change
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!video) return;

    if (!editingName.trim()) {
      window.alert('视频名称不能为空');
      return;
    }

    setUpdating(true);

    try {
      // 先更新名称
      const nameResponse = await fetch(`${API_BASE_URL}/video/update-name`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: video.videoId,
          name: editingName.trim()
        }),
      });

      if (nameResponse.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      const nameData: ApiResponse = await nameResponse.json();
      if (!nameData.success) {
        window.alert(nameData.error || '更新名称失败');
        return;
      }

      // 再更新可见性
      const visibilityResponse = await fetch(`${API_BASE_URL}/video/update-visibility`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: video.videoId,
          visibility: editingVisibility
        }),
      });

      const visibilityData: ApiResponse = await visibilityResponse.json();
      if (!visibilityData.success) {
        window.alert(visibilityData.error || '更新可见性失败');
        return;
      }

      // 词级高亮数据源（如果发生变化才请求）
      if ((video.wordHighlightMode || 'display') !== editingWordMode) {
        const wordModeResp = await fetch(`${API_BASE_URL}/video/update-word-highlight-mode`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            videoId: video.videoId,
            mode: editingWordMode,
          }),
        });
        const wordModeData: ApiResponse = await wordModeResp.json();
        if (!wordModeData.success) {
          window.alert(wordModeData.error || '更新词级高亮模式失败');
          return;
        }
        // 本地同步，避免下一次比较还判定为脏
        setVideo(prev => prev ? { ...prev, wordHighlightMode: editingWordMode } : prev);
      }

      // 更新成功，显示成功消息
      setSuccessMessage('保存成功');
      // 3秒后自动清除成功消息
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      window.alert('网络错误，请稍后重试');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <div className="video-edit-container">
        <div className="loading-message">加载中...</div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="video-edit-container">
        <div className="error-message">视频不存在</div>
      </div>
    );
  }

  const header = (
    <div className="video-edit-header app-shell-header-content">
      <button
        onClick={handleCancel}
        className="back-button"
        title="取消并返回"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </button>
      <h1>编辑视频</h1>
    </div>
  );

  return (
    <AppShell header={header} footer={null}>
      <div className="video-edit-container">
        <div className="video-edit-content">
        {/* 视频封面 */}
        <div className="video-cover-section">
          <div className="video-cover-large">
            <img
              src={localCoverPreview || getVideoCoverUrl(video)}
              alt={video.name || video.videoId}
              onError={handleCoverError}
              onClick={handleCoverClick}
              role="button"
              tabIndex={0}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleCoverSelected}
            />
            <div className="cover-edit-hint" onClick={handleCoverClick}>
              {uploadingCover ? '上传中...' : '更换封面'}
            </div>
            {video.capabilities?.hasWordAlignment && (
              <div className="video-badge-large">
                <span className="icon-inline" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2 5 5 .5-4 3 1 5-4-2-4 2 1-5-4-3 5-.5z"></path></svg></span>
                <span>词级对齐</span>
              </div>
            )}
          </div>
        </div>

        {/* 编辑表单 */}
        <div className="edit-form">
          <div className="form-group">
            <label className="form-label">视频ID</label>
            <div className="video-id-display">{video.videoId}</div>
          </div>

          <div className="form-group">
            <label className="form-label">视频名称</label>
            <Input
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              placeholder="请输入视频名称"
              disabled={updating}
            />
          </div>

          <div className="form-group">
            <label className="form-label">可见性</label>
            <Select
              value={editingVisibility}
              onValueChange={(value) => setEditingVisibility(value as 'private' | 'public')}
              disabled={updating}
            >
              <SelectItem value="private"><Lock size={14} className="icon-inline" /> 私有</SelectItem>
              <SelectItem value="public"><Globe size={14} className="icon-inline" /> 公开</SelectItem>
            </Select>
          </div>

          {video.capabilities?.hasWordAlignment && (
            <div className="form-group">
              <label className="form-label">词级高亮模式</label>
              <div className="word-mode-options">
                <label className={`word-mode-option ${editingWordMode === 'display' ? 'is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="wordHighlightMode"
                    value="display"
                    checked={editingWordMode === 'display'}
                    onChange={() => setEditingWordMode('display')}
                    disabled={updating}
                  />
                  <div className="word-mode-option-body">
                    <div className="word-mode-option-title">修复词级模式</div>
                    <div className="word-mode-option-desc">
                      使用 <code>display_words.json</code>（合并送り仮名/助词等，对日语阅读更友好，默认）
                    </div>
                  </div>
                </label>
                <label className={`word-mode-option ${editingWordMode === 'raw' ? 'is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="wordHighlightMode"
                    value="raw"
                    checked={editingWordMode === 'raw'}
                    onChange={() => setEditingWordMode('raw')}
                    disabled={updating}
                  />
                  <div className="word-mode-option-body">
                    <div className="word-mode-option-title">原始词级模式</div>
                    <div className="word-mode-option-desc">
                      使用 <code>words.json</code>（WhisperX 原始词级对齐，逐 token 高亮，最忠实于时间戳）
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {successMessage && <div className="success-message">{successMessage}</div>}

          <div className="form-actions">
            <button
              onClick={handleSave}
              className="save-button"
              disabled={updating}
            >
              {updating ? '保存中...' : '保存'}
            </button>
          </div>

          {/* 视频合集归属 */}
          <div className="form-group" style={{ marginTop: 24 }}>
            <label className="form-label">所属合集</label>
            {loadingMemberships ? (
              <div className="video-edit-collections-hint">加载中...</div>
            ) : memberships.length === 0 ? (
              <div className="video-edit-collections-empty">
                你还没有创建任何合集。
                <button
                  type="button"
                  className="subtitle-edit-link"
                  style={{ marginLeft: 8 }}
                  onClick={() => navigate('/collections/new')}
                >
                  新建合集 →
                </button>
              </div>
            ) : (
              <ul className="video-edit-collections">
                {memberships.map((m) => {
                  const langMismatch =
                    !!video.sourceLanguage && m.sourceLanguage !== video.sourceLanguage;
                  return (
                    <li key={m.id} className="video-edit-collection-row">
                      <label className="video-edit-collection-label">
                        <input
                          type="checkbox"
                          checked={m.inCollection}
                          disabled={togglingId === m.id}
                          onChange={() => handleToggleCollection(m)}
                        />
                        <span className="video-edit-collection-name">{m.name}</span>
                        <span className="video-edit-collection-lang">· {m.sourceLanguage}</span>
                        {m.visibility === 'public' && (
                          <span className="video-edit-collection-vis">公开</span>
                        )}
                        {m.inCollection && m.position !== null && (
                          <span className="video-edit-collection-pos">第 {m.position} 集</span>
                        )}
                      </label>
                      {langMismatch && (
                        <span className="video-edit-collection-warn" title="视频语言与合集语言不一致">
                          语言不匹配
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="form-group" style={{ marginTop: 24 }}>
            <label className="form-label">字幕</label>
            <button
              type="button"
              className="subtitle-edit-link"
              onClick={() => navigate(`/video/edit/subtitles?videoId=${encodeURIComponent(video.videoId)}`)}
              disabled={updating}
            >
              编辑字幕 →
            </button>
          </div>
        </div>
        </div>
      </div>
    </AppShell>
  );
};

export default VideoEdit;
